import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

function DiscountSummary({ discountType, discountValue, estimatedTotal }) {
  let finalAmount = estimatedTotal;
  let discountAmount = 0;
  if (discountType === 'percentage') {
    discountAmount = estimatedTotal * (parseFloat(discountValue) / 100);
    finalAmount = estimatedTotal - discountAmount;
  } else if (discountType === 'fixed') {
    discountAmount = parseFloat(discountValue);
    finalAmount = estimatedTotal - discountAmount;
  }
  const isInvalid = finalAmount <= 0;

  return (
    <div className="p-3 bg-gray-50 rounded-xl text-sm">
      <div className="space-y-1">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Original Total:</span>
          <span className="font-medium">${estimatedTotal.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Discount:</span>
          <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between pt-2 border-t">
          <span className="font-semibold">Final Amount:</span>
          <span className={`font-bold ${isInvalid ? 'text-red-600' : 'text-green-600'}`}>
            ${finalAmount.toFixed(2)}
          </span>
        </div>
        {isInvalid && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
            ⚠️ Invalid: Final amount cannot be zero or negative
          </div>
        )}
      </div>
    </div>
  );
}

export default function AssignCourtsDialog({
  booking, isOpen, onOpenChange, onOpenClick,
  selectedBooking,
  courtsAssigned, availableCourts, assignedCourtNumbers,
  getAvailableCourtNumbersForDropdown, onUpdateCourt,
  discountType, setDiscountType,
  discountValue, setDiscountValue,
  discountReason, setDiscountReason,
  onConfirm,
  compact = false,
}) {
  return (
    <Dialog open={isOpen && selectedBooking?.id === booking.id} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          onClick={onOpenClick}
          data-testid="assign-courts-button"
          size={compact ? 'sm' : 'default'}
          className={compact
            ? 'bg-emerald-700 hover:bg-emerald-800 text-white'
            : 'flex-1 btn-volt'}
        >
          Assign Courts
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="assign-courts-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase">Assign Courts</DialogTitle>
          <DialogDescription className="sr-only">
            Assign court numbers for this pending booking and optionally apply a discount before sending to the customer.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          {assignedCourtNumbers.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs font-bold text-amber-900 uppercase mb-1">
                ⚠️ Already Assigned for this time slot:
              </p>
              <div className="flex gap-2 flex-wrap">
                {assignedCourtNumbers.map((num) => (
                  <span key={num} className="px-2 py-1 bg-amber-200 text-amber-900 rounded-full text-xs font-bold">
                    Court {num}
                  </span>
                ))}
              </div>
            </div>
          )}

          {availableCourts.length === 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-900">
                ⚠️ No courts available for this time slot. All courts are already assigned.
              </p>
            </div>
          )}

          {courtsAssigned.map((court, idx) => {
            const availableForThisDropdown = getAvailableCourtNumbersForDropdown(idx);
            return (
              <div key={`${court.sport}-${idx}`}>
                <Label className="uppercase text-xs font-bold text-muted-foreground mb-2 block">
                  Court {idx + 1} - {court.sport}
                </Label>
                <Select value={court.court_number.toString()} onValueChange={(val) => onUpdateCourt(idx, val)}>
                  <SelectTrigger data-testid={`court-select-${idx}`} className="h-12 rounded-2xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableCourts.length > 0 ? (
                      availableForThisDropdown.map((num) => (
                        <SelectItem key={num} value={num.toString()}>Court {num} ✓</SelectItem>
                      ))
                    ) : (
                      [1, 2, 3, 4, 5].map((num) => {
                        const isSelectedElsewhere = courtsAssigned.some((c, i) => i !== idx && c.court_number === num);
                        const isAssignedToOther = assignedCourtNumbers.includes(num);
                        return (
                          <SelectItem
                            key={num}
                            value={num.toString()}
                            disabled={isAssignedToOther || isSelectedElsewhere}
                          >
                            Court {num} {isAssignedToOther ? '(Assigned)' : isSelectedElsewhere ? '(Selected)' : ''}
                          </SelectItem>
                        );
                      })
                    )}
                  </SelectContent>
                </Select>
              </div>
            );
          })}

          <div className="pt-4 border-t">
            <Label className="uppercase text-xs font-bold text-muted-foreground mb-3 block">
              Discount (Optional)
            </Label>
            <div className="space-y-3">
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger className="h-10 rounded-xl">
                  <SelectValue placeholder="No Discount" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Discount</SelectItem>
                  <SelectItem value="percentage">Percentage (%)</SelectItem>
                  <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                </SelectContent>
              </Select>

              {discountType !== 'none' && (
                <>
                  <Input
                    type="number"
                    min="0"
                    step={discountType === 'percentage' ? '1' : '0.01'}
                    placeholder={discountType === 'percentage' ? 'Enter percentage (e.g., 20)' : 'Enter amount (e.g., 10.00)'}
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="h-10 rounded-xl"
                  />
                  <Input
                    type="text"
                    placeholder="Reason (e.g., Loyalty discount, First-time customer)"
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    className="h-10 rounded-xl"
                  />
                  {discountValue > 0 && selectedBooking?.estimated_total && (
                    <DiscountSummary
                      discountType={discountType}
                      discountValue={discountValue}
                      estimatedTotal={selectedBooking.estimated_total}
                    />
                  )}
                </>
              )}
            </div>
          </div>

          <Button onClick={onConfirm} data-testid="confirm-assign-button" className="w-full btn-volt">
            Send to Customer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
